import React, { useEffect, useRef, useState } from "react";
import "./Editor.css";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Heading from "@tiptap/extension-heading";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Underline from "@tiptap/extension-underline";
import Strike from "@tiptap/extension-strike";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import { Container, Flex } from "../../layout";
import { Text } from "../../ui";
import {
  TbBold,
  TbItalic,
  TbLink,
  TbList,
  TbListNumbers,
  TbStrikethrough,
  TbUnderline,
  TbPhoto,
  TbHighlight,
} from "react-icons/tb";

const Editor = ({ label, setImageFiles, onChange, value }) => {
  const [isActive, setIsActive] = useState(false);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Heading.configure({ levels: [4] }),
      Bold,
      Italic,
      Underline,
      Strike,
      Highlight.configure({ multicolor: true }),
      Link.configure({ openOnClick: false }),
      BulletList,
      OrderedList,
      ListItem,
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      handleDrop(view, event, slice, moved) {
        event.preventDefault();
        const files = event.dataTransfer.files;
        if (files.length) {
          Array.from(files).forEach((file) => {
            const url = URL.createObjectURL(file);
            editor.chain().focus().setImage({ src: url }).run();
            setImageFiles((prev) => [...prev, { file, url }]);
          });
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML() && value !== undefined) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  const handleFocus = () => {
    setIsActive(true);
    inputRef.current?.focus();
  };

  const handleImageUpload = (event) => {
    const files = event.target.files;
    if (files.length) {
      Array.from(files).forEach((file) => {
        const url = URL.createObjectURL(file);
        editor.chain().focus().setImage({ src: url }).run();
        setImageFiles((prev) => [...prev, { file, url }]);
      });
    }
  };

  return (
    <Container className="Editor">
      {label && (
        <Text size="sm" className="text-sm" onClick={handleFocus}>
          {label}
        </Text>
      )}

      <Container className={`ctn-editor ${isActive ? "active" : ""}`}>
        {editor && (
          <Flex className="toolbar" gap={10}>
            <Flex
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 4 }).run()
              }
              className={
                editor.isActive("heading", { level: 4 }) ? "active" : ""
              }
            >
              Titre
            </Flex>
            <Flex
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={editor.isActive("bold") ? "active" : ""}
            >
              <TbBold />
            </Flex>
            <Flex
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={editor.isActive("italic") ? "active" : ""}
            >
              <TbItalic />
            </Flex>
            <Flex
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={editor.isActive("underline") ? "active" : ""}
            >
              <TbUnderline />
            </Flex>
            <Flex
              onClick={() => editor.chain().focus().toggleStrike().run()}
              className={editor.isActive("strike") ? "active" : ""}
            >
              <TbStrikethrough />
            </Flex>
            <Flex
              onClick={() => editor.chain().focus().toggleHighlight().run()}
              className={editor.isActive("highlight") ? "active" : ""}
            >
              <TbHighlight />
            </Flex>
            <Flex onClick={() => fileInputRef.current.click()}>
              <TbPhoto />
            </Flex>
            <Flex
              type="center"
              onClick={() => {
                const url = prompt("Entrez l'URL du lien :");
                if (url) {
                  editor.chain().focus().setLink({ href: url }).run();
                }
              }}
              className={editor.isActive("link") ? "active" : ""}
            >
              <TbLink />
            </Flex>
            <Flex
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={editor.isActive("bulletList") ? "active" : ""}
            >
              <TbList />
            </Flex>
            <Flex
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={editor.isActive("orderedList") ? "active" : ""}
            >
              <TbListNumbers />
            </Flex>
          </Flex>
        )}

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept="image/*"
          onChange={handleImageUpload}
          multiple
        />
        <EditorContent editor={editor} ref={inputRef} />
      </Container>
    </Container>
  );
};

export default Editor;
